import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormCacheService } from '@shared/services/form-cache.service';
import { Column } from '@shared/models/column.model';
@Component({
  selector: 'harp-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {
  @Input() columns;
  @Input() pageNumber = 1;
  @Input('pageSize') originalPageSize = 10;
  @Input() isAsc = false;
  @Input() sortColumnName;
  @Input() data;
  @Input() formName;
  @Input() totalEntries;
  @Input() injector;
  @Input() showCustomPageSize = false;
  @Input() pageSizes = [10, 20, 50];
  @Input() idKey;
  @Input() hasCheckbox = false;
  @Input() getCheckboxLabel?: (unknown) => string;
  @Input() hasPagination = true;
  // Note: this uses proper pagination with max limit on the API call
  @Input() usePagination = false;
  @Output() entrySelectedEvent = new EventEmitter<any[]>();
  @Input() customCompare;
  @Input() tableName!: string;
  @Output() pageChangeHandler = new EventEmitter<any[]>();
  @Input() maskData = false;

  pageSize;
  sortedData;
  checkedEntries = [];
  allChecked = false;

  rowMaxLineCount = new Map(); // NOTE: tracks max cell height for a row

  constructor(private formCacheService: FormCacheService) {}

  ngOnInit(): void {
    this.initTable();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const prop in changes) {
      this[prop] = changes[prop].currentValue;
    }

    // NOTE: previousValue < currentValue when an entry is removed from the table
    if (
      changes?.totalEntries?.currentValue < changes?.totalEntries?.previousValue
    ) {
      this.checkedEntries = [];
      this.formCacheService.set(this.formName, {
        checkedEntries: this.checkedEntries,
      });
    }

    this.initTable();
  }

  private initTable() {
    if (this.customCompare) {
      this.compare = this.customCompare;
    }

    this.data = this.data.map((item, index) => ({ ...item, rowId: index }));
    this.sortedData = this.data;

    this.setDefaults();
    this.updatePageByCachedValues();

    this.checkedEntries = this.checkedEntries.filter(e =>
      this.data.some(d => d[this.idKey] === e[this.idKey]),
    );
  }

  onCheckboxChange(entry) {
    if (this.isChecked(entry)) {
      this.checkedEntries = this.checkedEntries.filter(
        e => e[this.idKey] !== entry[this.idKey],
      );
    } else {
      this.checkedEntries = [...this.checkedEntries, entry];
    }
    this.formCacheService.set(this.formName, {
      checkedEntries: this.checkedEntries,
    });
    this.allChecked = this.checkedEntries.length === this.data.length;
    this.entrySelectedEvent.emit(this.checkedEntries);
  }

  isChecked(entry) {
    return this.checkedEntries.some(e => e[this.idKey] === entry[this.idKey]);
  }

  toggleCheckAll() {
    this.allChecked = !this.allChecked;
    if (!this.allChecked) {
      this.checkedEntries = [];
    } else {
      this.checkedEntries = [...this.data];
    }
    this.formCacheService.set(this.formName, {
      checkedEntries: this.checkedEntries,
    });
    this.entrySelectedEvent.emit(this.checkedEntries);
  }

  private setDefaults() {
    this.columns = this.columns.map(col => {
      if (!('canSort' in col)) {
        col.canSort = true;
      }

      if (!('showHeader' in col)) {
        col.showHeader = true;
      }

      return col;
    });
  }

  updatePageByCachedValues() {
    const form = this.formCacheService.get(this.formName);

    this.sortColumnName = form.sortColumnName || this.sortColumnName;
    this.isAsc = form.isAsc ?? this.isAsc;
    this.pageSize = this.hasPagination
      ? form.pageSize || this.originalPageSize
      : this.totalEntries;

    this.checkedEntries = form.checkedEntries || this.checkedEntries;
    this.entrySelectedEvent.emit(this.checkedEntries);

    if (this.sortColumnName) {
      this.sortBy(this.sortColumnName, this.isAsc);
    } else {
      this.sortBy(this.columns[0].key, false);
    }

    const maxPossiblePage = Math.ceil(this.totalEntries / this.pageSize);
    const pageNumber = form.pageNumber || this.pageNumber;

    this.pageNumber = pageNumber <= maxPossiblePage ? pageNumber : 1;
  }

  onPageChange(pageNumber) {
    this.formCacheService.set(this.formName, { pageNumber });
    this.pageNumber = pageNumber;
    this.pageChangeHandler.emit(pageNumber);
  }

  sortBy(columnName, isAsc): void {
    const sortedData = [...(this.data || [])].sort(this.compare(columnName));

    if (isAsc) {
      this.sortedData = sortedData;
    } else {
      this.sortedData = [...sortedData].reverse();
    }

    this.isAsc = isAsc;
    this.sortColumnName = columnName;

    this.formCacheService.set(this.formName, {
      isAsc: this.isAsc,
      sortColumnName: this.sortColumnName,
    });
  }

  private compare(key): (a, b) => -1 | 0 | 1 {
    return function (a, b) {
      if (a[key]?.toString().toLowerCase() > b[key]?.toString().toLowerCase())
        return 1;
      if (a[key]?.toString().toLowerCase() < b[key]?.toString().toLowerCase())
        return -1;
      return 0;
    };
  }

  format(value, pipes) {
    if (!pipes) {
      return value;
    }

    return pipes.reduce(
      (acc, { token, params }) =>
        this.injector.get(token).transform(acc, ...(params || [])),
      value,
    );
  }

  updatePageSize(size) {
    const pageSize = this.pageSizes[size];
    this.formCacheService.set(this.formName, { pageSize });
    this.pageSize = pageSize;
  }

  getPage() {
    const page = this.pageSizes.indexOf(this.pageSize) + 1;
    return page === -1 ? 1 : page;
  }

  getContentId(columnKey, rowId) {
    return `${columnKey}-${rowId}`;
  }

  getTooltipIcon(column) {
    return column?.tooltip?.icon || 'ion-md-chatboxes';
  }

  /**
   * NOTE: This populates rowMaxLineCount with the largest line count for
   * the row but skips over columns with the truncate flag set
   */
  maxHeight(
    colRef: HTMLElement,
    column: Column,
    rowNum: number,
    shouldReturn = true,
  ) {
    if (column.truncate) {
      return shouldReturn ? Math.round(this.rowMaxLineCount.get(rowNum)) : '';
    }

    const mh = this.rowMaxLineCount.get(rowNum) ?? 0;

    const divHeight = colRef.children.item(0).clientHeight;

    const { lineHeight } = window.getComputedStyle(colRef);

    const lineCount = divHeight / parseInt(lineHeight);

    this.rowMaxLineCount.set(rowNum, Math.max(mh, lineCount));

    if (shouldReturn) {
      return this.rowMaxLineCount.get(rowNum);
    }
  }

  // NOTE: This determines if there is enough content to warrant truncation
  shouldRenderTruncateButton(colRef: HTMLElement, rowNum: number) {
    const divHeight = colRef.children.item(0).clientHeight;

    const { lineHeight } = window.getComputedStyle(colRef);

    const lineCount = divHeight / parseInt(lineHeight);

    return lineCount >= Math.round(this.rowMaxLineCount.get(rowNum));
  }

  /**
   * NOTE: This is used to force a rerender when the window is
   * resized to determine the new truncation row count
   */
  @HostListener('window:resize', ['$event'])
  onResize() {}
}
